import { Controller } from '@hotwired/stimulus'
import { createApp } from '@app/adapters/create-app'
import SearchInput from '@app/application/report/screens/index/components/search-input.vue'

export default class extends Controller {
  static targets = ['search', 'emptyState', 'pinnedSection']

  declare searchTarget: HTMLElement
  declare hasSearchTarget: boolean
  declare emptyStateTarget: HTMLElement
  declare pinnedSectionTarget: HTMLElement
  declare hasPinnedSectionTarget: boolean

  private searchApp: ReturnType<typeof createApp> | null = null

  connect(): void {
    this.mountSearchInput()
  }

  disconnect(): void {
    this.searchApp?.unmount()
    this.searchApp = null
  }

  private mountSearchInput(): void {
    if (!this.hasSearchTarget) return

    this.searchApp = createApp(SearchInput, {
      onSearch: (value: string): void => this.search(value),
    })
    this.searchApp.mount(this.searchTarget)
  }

  // Root-scoped so the Vue-rendered pinned section's cards filter by name
  // like list rows.
  private search(value: string): void {
    const items = Array.from(
      this.element.querySelectorAll<HTMLElement>('[data-report-name]')
    )
    const categories = Array.from(
      this.element.querySelectorAll<HTMLElement>('[data-category-title]')
    )

    items.forEach((item) => {
      const name = item.dataset.reportName ?? ''
      item.style.display = this.matches(value, name) ? '' : 'none'
    })

    let hiddenCategoriesCount = 0

    categories.forEach((category) => {
      const categoryTitle = category.dataset.categoryTitle ?? ''
      const hasVisibleItem = items.some(
        (item) =>
          item.dataset.reportCategory === categoryTitle &&
          item.style.display !== 'none'
      )
      category.style.display = hasVisibleItem ? '' : 'none'
      if (!hasVisibleItem) hiddenCategoriesCount++
    })

    // Unlike list categories (flat siblings matched by name), the pinned
    // section wraps its cards, so its visibility is derived structurally —
    // category names are free-text firm data and can't be a reliable key.
    let pinnedSectionVisible = false
    if (this.hasPinnedSectionTarget) {
      pinnedSectionVisible = Array.from(
        this.pinnedSectionTarget.querySelectorAll<HTMLElement>(
          '[data-report-name]'
        )
      ).some((item) => item.style.display !== 'none')
      this.pinnedSectionTarget.style.display = pinnedSectionVisible
        ? ''
        : 'none'
    }

    this.emptyStateTarget.style.display =
      hiddenCategoriesCount === categories.length && !pinnedSectionVisible
        ? ''
        : 'none'
  }

  private matches(input: string, name: string): boolean {
    const lowerName = name.toLowerCase()
    return input
      .toLowerCase()
      .split(/\s+/)
      .every((word) => lowerName.includes(word))
  }
}
